import { useEffect, useRef } from 'react';
import classnames from 'classnames';

import { useDisabled } from '@hh.ru/magritte-common-use-disabled';
import { FcWithFlag } from '@hh.ru/magritte-types';
import { CheckboxSkeleton } from '@hh.ru/magritte-ui-checkbox-radio/CheckboxSkeleton';
import { CheckboxProps } from '@hh.ru/magritte-ui-checkbox-radio/types';
import { CheckboxCheckSize24, CheckboxLineSize24 } from '@hh.ru/magritte-ui-icon/variants/private';
import { enrichWithSkeleton } from '@hh.ru/magritte-ui-skeleton';

import styles from './checkbox.less';

const CheckboxComponent: FcWithFlag<CheckboxProps, 'isCheckbox'> = ({
    checked,
    disabled,
    invalid,
    indeterminate = false,
    onChange,
    readOnly,
    'aria-label': ariaLabel,
    'data-qa': dataQaInput,
    dataQaContainer = 'checkbox-container',
    dataQaCheckbox = 'checkbox',
    ...props
}) => {
    const forceDisabled = useDisabled();
    const checkboxRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!checkboxRef.current) {
            return;
        }
        checkboxRef.current.indeterminate = indeterminate;
    }, [indeterminate]);

    return (
        <span className={styles.checkboxContainer} data-qa={dataQaContainer}>
            <input
                {...props}
                checked={checked}
                data-qa={dataQaInput}
                // Checkable Card вызывает event.preventDefault(),
                // из-за чего браузер не перерисовывает чекбокс при изменении пропа checked,
                // поэтому в controlled чекбоксах полагаемся на CSS-класс, а не на селектор :checked
                className={classnames(styles.checkboxInput, {
                    [styles.checkboxInputChecked]: checked,
                    // нужно отличать false от undefined, потому что в случае uncontrolled чекбокса
                    // checked равен undefined и значение задается через проп defaultChecked
                    [styles.checkboxInputUnchecked]: Object.is(checked, false),
                })}
                ref={checkboxRef}
                readOnly={readOnly}
                type="checkbox"
                aria-label={ariaLabel}
                disabled={forceDisabled || !!disabled}
                onChange={onChange}
            />
            <span
                className={classnames(styles.checkbox, {
                    [styles.invalid]: invalid,
                })}
                data-qa={dataQaCheckbox}
            />
            <span className={styles.iconWrapper}>
                <span
                    className={classnames(styles.icon, {
                        [styles.iconHidden]: !indeterminate,
                    })}
                >
                    <CheckboxLineSize24 />
                </span>
                <span
                    className={classnames(styles.icon, {
                        [styles.iconHidden]: indeterminate,
                    })}
                >
                    <CheckboxCheckSize24 />
                </span>
            </span>
        </span>
    );
};

CheckboxComponent.isCheckbox = true;

export const Checkbox = enrichWithSkeleton(CheckboxComponent, CheckboxSkeleton);
